import { Box, Button, LinearProgress, Stack, Step, StepLabel, Stepper, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';
import { ArrowBack, ArrowForward, CheckCircle } from '@mui/icons-material';
import { DISPATCH } from './dispatchTheme';

export function DispatchStepHeader({ steps, activeStep }: { steps: string[]; activeStep: number }) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const pct = Math.round(((activeStep + 1) / steps.length) * 100);
  return (
    <Box mb={2.5}>
      <Stack direction="row" justifyContent="space-between" mb={1}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">Step {activeStep + 1} of {steps.length}</Typography>
        <Typography variant="caption" fontWeight={800} color={DISPATCH.primary}>{pct}%</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ mb: 2, height: 6, borderRadius: 3, bgcolor: alpha(DISPATCH.primary, 0.08), '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: DISPATCH.primary } }} />
      <Stepper activeStep={activeStep} alternativeLabel={!mobile} orientation={mobile ? 'vertical' : 'horizontal'}
        sx={{ '& .MuiStepLabel-label': { fontSize: mobile ? '0.75rem' : '0.68rem', fontWeight: 600 } }}>
        {steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>
    </Box>
  );
}

export default function DispatchStepNav({
  activeStep, stepValid, onBack, onNext, isLastStep, lastStepHint,
}: {
  activeStep: number;
  stepValid: boolean;
  onBack: () => void;
  onNext: () => void;
  isLastStep?: boolean;
  lastStepHint?: string;
}) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Stack direction={mobile ? 'column' : 'row'} spacing={mobile ? 1 : 0} justifyContent="space-between" alignItems={mobile ? 'stretch' : 'center'} pt={2} mt={1} borderTop={`1px solid ${DISPATCH.border}`}>
      <Button startIcon={<ArrowBack />} onClick={onBack} disabled={activeStep === 0} fullWidth={mobile}
        sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 2.5, order: mobile ? 2 : 0 }}>Back</Button>
      {isLastStep ? (
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ flex: 1, textAlign: 'center', px: 1, order: 1 }}>{lastStepHint || 'Confirm using the button below'}</Typography>
      ) : (
        <Button variant="contained" endIcon={<ArrowForward />} onClick={onNext} disabled={!stepValid} fullWidth={mobile}
          sx={{ fontWeight: 800, textTransform: 'none', borderRadius: 2.5, px: 3, order: mobile ? 1 : 0, bgcolor: DISPATCH.primary, boxShadow: `0 6px 20px ${alpha(DISPATCH.primary, 0.3)}` }}>
          Continue
        </Button>
      )}
    </Stack>
  );
}

export function DispatchSummaryCard({ title, rows, accent }: { title: string; rows: { label: string; value: string }[]; accent?: string }) {
  return (
    <Box sx={{ p: 1.25, borderRadius: `${DISPATCH.radiusSm}px`, border: `1px solid ${accent ? alpha(accent, 0.25) : DISPATCH.border}`, bgcolor: accent ? alpha(accent, 0.04) : DISPATCH.bgMuted }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={0.75}>
        <CheckCircle sx={{ fontSize: 16, color: accent || DISPATCH.primary }} />
        <Typography variant="caption" fontWeight={800}>{title}</Typography>
      </Stack>
      <Stack spacing={0.5}>
        {rows.map((r) => (
          <Stack key={r.label} direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>{r.label}</Typography>
            <Typography variant="caption" fontWeight={700} textAlign="right">{r.value}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
